import React from "react";
import { BrowserRouter as Router, Route, Switch } from "react-router-dom";
import { styled } from "@stitches/react";
import "@fontsource/roboto";

import { UserProvider } from "../contexts/UserContext";
import NavBar from "../components/navbar/NavBar";
import Footer from "../components/footer/Footer";
import RegisterView from "../views/RegisterView";
import LoginView from "../views/LoginView";
import ForgotView from "../views/ForgotPasswordView";
import { NewOrderView } from "../views/NewOrderView";
import ResetView from "../views/ResetPasswordView";
import { OrdersView } from "../views/OrdersView";
import { AdminOrdersView } from "../views/AdminOrdersView";

import { SmartWaterView } from "../views/SmartWaterView/SmartWaterView";
import CustomerCalculatorView from "../views/CustomerCalculatorView/CustomerCalculatorView";
import WaterQualityView from "../views/WaterQualityView";
// TODO: move these to client/views/
import Homepage from "../components/semantic_ui/Homepage/Homepage";
import Aboutpage from "../components/semantic_ui/Aboutpage/Aboutpage";

import EmailRoute from "./EmailRoute";
import PrivateRoute from "./PrivateRoute";
import { AdminRoute } from "./AdminRoute";
import { AdminDeliveriesView } from "../views/AdminDeliveriesView";
import { AdminNewUserView } from "../views/AdminNewUserView";
import { AdminCustomersView } from "../views/AdminCustomersView";
import { NotFoundView } from "../views/NotFoundView";
import { UserProfilePage } from "../views/UserProfilePage/UserProfilePage";
import { UserProfileEditPage } from "../views/UserProfilePage/UserProfileEditPage";
import { UserRoute } from "./UserRoute";

// Customer order confirmation view
import OrderConfirmationResponseView from "../views/OrderConfirmationView/OrderConfirmationResponseView";
import { ModifyDeliveryView } from "../views/ModifyDeliveryView/ModifyDeliveryView";
import AdminSettingsView from "../views/AdminSettingsView/AdminSettingsView";
import { AdminCreateOrderView } from "../views/AdminCreateOrderView/AdminCreateOrderView";
import PrivacyView from "../views/PrivacyView/PrivacyView";
import { PaymentView } from "../views/PaymentView";
import { PaymentHistoryPage } from "../views/PaymentHistoryPage/PaymentHistoryPage";
import ContactView from "../views/ContactView/ContactView";
import { AdminComplianceView } from "../views/AdminComplianceView";

import { RouteAnalytics } from "../components/analytics/RouteAnalytics";

const App: React.FC = () => {
  return (
    <UserProvider>
      <Router>
        <RouteAnalytics />
        <AppWrapper>
          <NavBar />
          <MainWrapper>
            <Switch>
              <Route exact path="/" component={Homepage} />
              <Route exact path="/about-us" component={Aboutpage} />
              {/* The component below has some nice hero sections we can steal for the homepage */}
              <Route exact path="/register" component={RegisterView} />
              <Route exact path="/login" component={LoginView} />
              <Route exact path="/forgotpassword" component={ForgotView} />
              <Route
                exact
                path="/resetpassword/:email/:token"
                component={ResetView}
              />

              <Route exact path="/smartwater" component={SmartWaterView} />
              <Route exact path="/customercalculator" component={CustomerCalculatorView} />
              <Route exact path="/water-quality" component={WaterQualityView} />
              <Route exact path="/privacy" component={PrivacyView} />
              <Route exact path="/contact" component={ContactView} />
              {/* Public payment landing page from invoice email token link. */}
              <Route exact path="/pay/:token" component={PaymentView} />
              <EmailRoute exact path="/confirm-job-order/:jobId/:action" component={OrderConfirmationResponseView} message="Login first to confirm your order" />
              <EmailRoute exact path="/modify-delivery/:jobId" component={ModifyDeliveryView} message="Login first to modify your order" />

              <PrivateRoute exact path="/orders" component={OrdersView} />
              <PrivateRoute exact path="/neworder" component={NewOrderView} />

              <AdminRoute
                exact
                path="/admin/orders"
                component={AdminOrdersView}
              />
              <AdminRoute
                exact
                path="/admin/deliveries"
                component={AdminDeliveriesView}
              />
              <AdminRoute
                exact
                path="/admin/settings"
                component={AdminSettingsView}
              />
              <AdminRoute
                exact
                path="/admin/customers"
                component={AdminCustomersView}
              />
              {/* Phase 7: single admin workspace for compliance data entry, review, and export history. */}
              <AdminRoute
                exact
                path="/admin/compliance"
                component={AdminComplianceView}
              />
              <AdminRoute
                exact
                path="/admin/create-order/:userId"
                component={AdminCreateOrderView}
              />
              <AdminRoute
                exact
                path="/admin/new-user"
                component={AdminNewUserView}
              />
              <UserRoute
                exact
                path="/profile"
                component={UserProfilePage}
              />
              <UserRoute
                exact
                path="/profile/edit"
                component={UserProfileEditPage}
              />
              {/* Payment history stays nested under profile so customer navigation does not grow with
                  every account feature added in Phase 6. */}
              <UserRoute
                exact
                path="/profile/payment-history"
                component={PaymentHistoryPage}
              />
              <Route>
                <NotFoundView />
              </Route>
            </Switch>
          </MainWrapper>
          <Footer />
        </AppWrapper>
      </Router>
    </UserProvider>
  );
};

export default App;

const AppWrapper = styled("div", { height: "100%", minHeight: "100vh", display: "flex", flexDirection: "column" });
const MainWrapper = styled("div", { height: "100%", flexGrow: 1 });
